/**
 * FormField Component - Wrapper around shadcn/ui Label for backward compatibility
 * Uses shadcn/ui Label component with improved styling
 */
import { memo, isValidElement, cloneElement } from 'react';
import type { ReactElement } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  id?: string;
  label?: React.ReactNode;
  required?: boolean;
  error?: string;
  children?: React.ReactNode;
  helpText?: string;
  className?: string;
}

const FormField = memo(({
  id,
  label,
  required = false,
  error,
  children,
  helpText,
  className
}: FormFieldProps) => {
  const errorId = id ? `${id}-error` : undefined;

  // Wire aria-invalid / aria-required / aria-describedby through to the
  // underlying control so assistive tech announces the field state.
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement, {
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
        'aria-describedby': error
          ? cn((children as ReactElement).props['aria-describedby'], errorId)
          : (children as ReactElement).props['aria-describedby'],
      })
    : children;

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={id} className={cn(error && "text-destructive")}>
        {label} {required && <span className="text-destructive" aria-hidden="true">*</span>}
      </Label>
      {control}
      {error && (
        <p id={errorId} role="alert" className="text-sm font-medium text-destructive">{error}</p>
      )}
      {helpText && !error && (
        <p className="text-sm text-muted-foreground">{helpText}</p>
      )}
    </div>
  );
});

FormField.displayName = 'FormField';

export default FormField;
